import type { AiFeatureMeta } from './features';
import type { AiModelSettings } from './modelSettings';
import type { AiReasoningEffort } from './models';
import { getAiModel } from './models';
import type { AiUsageComboStats, AiUsageStats } from './usageStats';
import { AI_USAGE_STATS_MIN_SAMPLES } from './usageStats';

/** Fallback reasoning tokens per effort, scaled per feature. Seeded from deck power runs on GPT-5.6. */
export const REASONING_TOKENS_BY_EFFORT: Record<AiReasoningEffort, number> = {
  none: 0,
  low: 60,
  medium: 160,
  high: 320,
  xhigh: 900,
  max: 1_800,
};

export type AiEstimateSource = 'stats' | 'stats-all-versions' | 'profile';

export interface AiRunEstimate {
  input_tokens: number;
  cached_input_tokens: number;
  /** Includes reasoning tokens, as the API reports and bills them. */
  output_tokens: number;
  reasoning_tokens: number;
  cost_usd: number;
  /** Null until any run of this model has been aggregated. */
  duration_ms: number | null;
  source: AiEstimateSource;
  sample_size: number;
}

export interface AiCostUsage {
  input_tokens: number;
  cached_input_tokens: number;
  output_tokens: number;
  cache_write_tokens?: number;
}

/** USD cost at the model's current prices; null for models no longer in the registry. */
export function computeCostUsd(modelId: string, usage: AiCostUsage | null): number | null {
  const model = getAiModel(modelId);
  if (!model || !usage) return null;

  const { pricing } = model;
  // Without a cache-write rate, written tokens bill as ordinary input.
  const cacheWrite = pricing.cacheWritePerMTok === null ? 0 : (usage.cache_write_tokens ?? 0);
  const freshInput = Math.max(usage.input_tokens - usage.cached_input_tokens - cacheWrite, 0);

  const cost =
    (freshInput * pricing.inputPerMTok +
      usage.cached_input_tokens * pricing.cachedInputPerMTok +
      cacheWrite * (pricing.cacheWritePerMTok ?? 0) +
      usage.output_tokens * pricing.outputPerMTok) /
    1_000_000;

  return Number(cost.toFixed(6));
}

export function findComboStats(
  stats: AiUsageStats | null,
  settings: Pick<AiModelSettings, 'model' | 'effort' | 'verbosity'>,
): AiUsageComboStats | null {
  return (
    stats?.combos.find(
      (combo) => combo.model === settings.model && combo.effort === settings.effort && combo.verbosity === settings.verbosity,
    ) ?? null
  );
}

export function estimateInputTokens(feature: AiFeatureMeta, inputChars: number, stats: AiUsageStats | null): number {
  const fit = stats?.input;
  if (fit) return Math.round(fit.fixed_tokens + fit.tokens_per_char * inputChars);
  return feature.estimate.fixedInputTokens + Math.ceil(inputChars / 4);
}

export interface EstimateAiRunParams {
  feature: AiFeatureMeta;
  settings: AiModelSettings;
  /** Characters of the variable (non-system) input that will be sent. */
  inputChars: number;
  stats: AiUsageStats | null;
}

function fromCombo(combo: AiUsageComboStats) {
  return {
    outputTokens: combo.median_output_tokens,
    reasoningTokens: combo.median_reasoning_tokens,
    cachedRatio: combo.median_cached_ratio,
    durationMs: combo.median_duration_ms as number | null,
    source: (combo.scope === 'current' ? 'stats' : 'stats-all-versions') as AiEstimateSource,
    sampleSize: combo.sample_size,
  };
}

/**
 * Pre-call estimate shared by the dialog and the server: aggregated medians for the exact settings
 * when there are enough runs, otherwise the feature's static profile.
 */
export function estimateAiRun({ feature, settings, inputChars, stats }: EstimateAiRunParams): AiRunEstimate {
  const inputTokens = estimateInputTokens(feature, inputChars, stats);
  const combo = findComboStats(stats, settings);

  let estimate: ReturnType<typeof fromCombo>;
  if (combo && combo.sample_size >= AI_USAGE_STATS_MIN_SAMPLES) {
    estimate = fromCombo(combo);
  } else {
    const reasoningTokens = Math.round(REASONING_TOKENS_BY_EFFORT[settings.effort] * feature.estimate.reasoningScale);
    const outputTokens = feature.estimate.outputTokens + reasoningTokens;
    const speed = stats?.models.find((model) => model.model === settings.model);
    estimate = {
      outputTokens,
      reasoningTokens,
      cachedRatio: 0,
      durationMs: speed ? Math.round(speed.median_ms_per_output_token * outputTokens) : null,
      source: 'profile',
      sampleSize: 0,
    };
  }

  const outputTokens = Math.min(estimate.outputTokens, settings.maxOutputTokens);
  const reasoningTokens = Math.min(estimate.reasoningTokens, outputTokens);
  const cachedInputTokens = Math.round(inputTokens * estimate.cachedRatio);

  return {
    input_tokens: inputTokens,
    cached_input_tokens: cachedInputTokens,
    output_tokens: outputTokens,
    reasoning_tokens: reasoningTokens,
    cost_usd:
      computeCostUsd(settings.model, { input_tokens: inputTokens, cached_input_tokens: cachedInputTokens, output_tokens: outputTokens }) ??
      0,
    duration_ms: estimate.durationMs,
    source: estimate.source,
    sample_size: estimate.sampleSize,
  };
}
